import React, { useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, TextField, IconButton, CircularProgress, Paper, Typography, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation } from '@tanstack/react-query';
import { PostAi } from '../api/ai';
import toast from 'react-hot-toast';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const AiChatScreen = () => {
  const [conversation, setConversation] = useState([]);
  const [text, setText] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { mutate, isPending } = useMutation({
    mutationFn: PostAi,
    onSuccess: async (data) => {
      const aiResponse = { text: data?.data?.data, type: 'ai' };
      setConversation((prev) => [...prev, aiResponse]);
      toast.success('AI response received.');
    },
    onError: () => {
      toast.error('Something went wrong. Please try again.');
    },
  });

  const onchngeText = useCallback((e) => {
    setText(e.target.value);
  }, []);

  const send = async () => {
    if (!text.trim()) return;

    // Add the user's message to the conversation
    const newMessage = { text, type: 'user' };
    setConversation((prev) => [...prev, newMessage]);
    setText('');

    try {
      // Format the conversation history for the API
      const conversationHistory = conversation
        .map((msg) => `${msg.type === 'user' ? 'You' : 'AI'}: ${msg.text}`)
        .join('\n');

      // Send the conversation history along with the new message
      await mutate({ prompt: `${conversationHistory}\nYou: ${text}` });
    } catch (error) {
      toast.error('Unable to send the message.');
    }
  };

  const handleKeyDown = (event) => {
    if (text.trim() === '' || isPending) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      send();
      event.preventDefault();
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  const renderMessage = (msg, index) => (
    <Box
      key={index}
      sx={{
        display: 'flex',
        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
        mb: 1.5, // Reduced margin bottom
        px: isMobile ? 1 : 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 1.5, // Reduced padding
          backgroundColor: msg.type === 'user' ? '#0078D4' : '#f1f1f1',
          color: msg.type === 'user' ? '#fff' : '#000',
          maxWidth: '80%',
          borderRadius: msg.type === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0', // Smaller radius
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Lighter shadow
          position: 'relative',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.9rem', // Smaller font size
        }}
      >
        <ReactMarkdown
          children={msg.text}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <Box sx={{ position: 'relative' }}>
                  <SyntaxHighlighter
                    style={materialDark}
                    language={match[1]}
                    children={String(children).replace(/\n$/, '')}
                    {...props}
                  />
                  <Tooltip title="Copy code" placement="top" arrow>
                    <IconButton
                      onClick={() => copyToClipboard(String(children))}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        color: 'white',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
          }}
        />
      </Paper>
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #1e1e2f, #2a2a3b)',
        color: '#e0e0e0',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      {/* Chat Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          pt: isMobile ? '6vh' : '8vh',
          px: isMobile ? 1 : 2,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#555',
            borderRadius: '3px',
          },
        }}
      >
        {conversation.length ? (
          conversation.map((msg, index) => renderMessage(msg, index))
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mt: 4, fontSize: '1rem', color: '#aaa' }}
          >
            Start chatting with GinAI 2.0 – futuristic, sleek, and ready for 2025. Experience the next-gen AI interaction!
          </Typography>
        )}
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: isMobile ? 1 : 2,
          py: 1.5,
          borderTop: '1px solid #444',
          background: '#1e1e2f',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.5)',
        }}
      >
        <TextField
          multiline
          minRows={1}
          maxRows={4}
          onKeyDown={handleKeyDown}
          onChange={onchngeText}
          value={text}
          placeholder="Type your futuristic message..."
          fullWidth
          variant="outlined"
          sx={{
            flex: 1,
            mr: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              backgroundColor: '#2a2a3b',
              color: '#e0e0e0',
              fontSize: '1rem',
              '& fieldset': {
                borderColor: '#555',
              },
              '&:hover fieldset': {
                borderColor: '#777',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#00bcd4',
              },
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={send}
          disabled={!text.trim() || isPending}
          sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            backgroundColor: '#00bcd4',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#0097a7',
            },
          }}
        >
          {isPending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default AiChatScreen;
